import { useState, useMemo, useEffect, useRef } from "react";
import { FileText, Package, CheckCircle, ArrowUpDown, Clock } from "lucide-react";
import styles from "./DepositPayment.module.css";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import AddDepositModal from "../../../components/Pricing&Rules/DepositPayment/AddDepositModal";
import AddExceptionModal from "../../../components/Pricing&Rules/DepositPayment/AddExceptionModal";
import { useToast } from "../../../context/ToastContext";
import {
  useDepositRules,
  useDepositExceptions,
  useCreateDepositRule,
  useUpdateDepositRule,
  useUpdateDepositRuleStatus,
  useDeleteDepositRule,
} from "../../../api/pricing/useDepositApi";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";

const TABS = [
  { key: "rules",      label: "Deposit Rules",       icon: <FileText size={13} />    },
  { key: "exceptions", label: "Product Exceptions",  icon: <Package size={13} />     },
  { key: "validity",   label: "Validity & Activation", icon: <CheckCircle size={13} /> },
];

const getDisplayStatus = (row, pendingChanges) =>
  pendingChanges[row.id] !== undefined ? pendingChanges[row.id] : row.status;

// ── Deposit Rules Tab ──────────────────────────────────────
const RulesTab = ({ data, refetch, loading: listLoading }) => {
  const { showToast } = useToast();
  const { execute: createRule, loading: createLoading } = useCreateDepositRule();
  const { execute: updateRule, loading: updateLoading } = useUpdateDepositRule();
  const { execute: updateStatus, loading: statusLoading } = useUpdateDepositRuleStatus();
  const { execute: deleteRule, loading: deleteLoading } = useDeleteDepositRule();

  const [search, setSearch]               = useState("");
  const [typeFilter, setTypeFilter]       = useState("");
  const [statusFilter, setStatusFilter]   = useState("");
  const [sortField, setSortField]         = useState(null);
  const [sortDir, setSortDir]             = useState("asc");
  const [pendingChanges, setPendingChanges] = useState({});
  const [modal, setModal]                 = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const isFirstSearchEffect = useRef(true);

  const hasPending = Object.keys(pendingChanges).length > 0;
  const saveLoading = statusLoading;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    refetch({ q: debouncedSearch || undefined });
  }, [debouncedSearch, refetch]);

  const applyToggle = (row) => {
    setPendingChanges((prev) => {
      const current = prev[row.id];
      const next = current !== undefined ? !current : !row.status;
      return { ...prev, [row.id]: next };
    });
  };

  const handleSaveToggles = async () => {
    try {
      for (const [id, status] of Object.entries(pendingChanges)) {
        await updateStatus(Number(id), status);
      }
      setPendingChanges({});
      await refetch();
      showToast("Changes saved successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    }
  };

  const handleModalSave = async (form) => {
    try {
      if (modal?.row) {
        await updateRule(modal.row.id, form);
        showToast("Deposit rule updated", "success");
      } else {
        await createRule(form);
        showToast("Deposit rule added", "success");
      }
      await refetch();
      setModal(null);
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to save", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRule(deleteTarget.id);
      showToast("Deposit rule deleted successfully", "success");
      setDeleteTarget(null);
      await refetch();
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to delete rule", "error");
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    let rows = data.filter((r) => {
      if (typeFilter && r.depositType !== typeFilter) return false;
      const s = getDisplayStatus(r, pendingChanges);
      if (statusFilter === "true"  && !s) return false;
      if (statusFilter === "false" &&  s) return false;
      return true;
    });
    if (sortField) {
      rows = [...rows].sort((a, b) =>
        sortDir === "asc"
          ? String(a[sortField]).localeCompare(String(b[sortField]))
          : String(b[sortField]).localeCompare(String(a[sortField]))
      );
    }
    return rows;
  }, [data, search, typeFilter, statusFilter, sortField, sortDir, pendingChanges]);

  const cols = [
    { key: "name",             label: "Rule Name" },
    { key: "depositType",      label: "Type" },
    { key: "depositValue",     label: "Value" },
    { key: "productDependent", label: "Product Dependent" },
    { key: "startDate",        label: "Start Date" },
    { key: "endDate",          label: "End Date" },
    { key: "status",           label: "Status" },
  ];

  return (
    <>
      <div className={styles.tableSection}>
        <div className={styles.tableToolbar}>
          <div className={styles.filterBar}>
            <input
              className={styles.searchInput}
              placeholder="Search deposit rules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <SelectBox
              placeholder="Type: All"
              value={typeFilter}
              options={[
                { label: "Type: All",    value: "" },
                { label: "Percentage",   value: "Percentage" },
                { label: "Fixed Amount", value: "Fixed" },
              ]}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <SelectBox
              placeholder="Status: All"
              value={statusFilter}
              options={[
                { label: "Status: All", value: "" },
                { label: "Active",      value: "true" },
                { label: "Inactive",    value: "false" },
              ]}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          <button className={styles.add} onClick={() => setModal({ row: null })}>
            + Add Rule
          </button>
        </div>

        <div className={styles.resultsSummary}>
          Showing {filtered.length} of {data.length} rules
        </div>

        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                {cols.map((col) => (
                  <th key={col.key} onClick={() => toggleSort(col.key)}>
                    <span className={styles.thInner}>
                      {col.label} <ArrowUpDown size={11} style={{ opacity: 0.4 }} />
                    </span>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr><td colSpan={cols.length + 1} className={styles.noResults}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={cols.length + 1} className={styles.noResults}>No deposit rules found.</td></tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    {cols.map((col) => (
                      <td key={col.key}>
                        {col.key === "status" ? (
                          <ToggleSwitch
                            checked={!!getDisplayStatus(row, pendingChanges)}
                            onChange={() => applyToggle(row)}
                          />
                        ) : col.key === "depositValue" ? (
                          <span className={styles.valueChip}>
                            {row.depositType === "Percentage" ? `${row.depositValue}%` : `€${row.depositValue}`}
                          </span>
                        ) : col.key === "productDependent" ? (
                          <span className={`${styles.pill} ${row.productDependent ? styles.pillYes : styles.pillNo}`}>
                            {row.productDependent ? "Yes" : "No"}
                          </span>
                        ) : (
                          row[col.key] ?? "—"
                        )}
                      </td>
                    ))}
                    <td>
                      <div className={styles.rowActions}>
                        <button onClick={() => setModal({ row })}>Edit</button>
                        <button
                          className={styles.rowActionDelete}
                          onClick={() => setDeleteTarget({ id: row.id, name: row.name })}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.footerBar}>
          <span className={styles.paginationInfo}>{data.length} total rules</span>
          <button className={styles.saveChangesBtn} onClick={handleSaveToggles} disabled={!hasPending || saveLoading}>
            {saveLoading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {modal && (
        <AddDepositModal
          open={!!modal}
          onClose={() => setModal(null)}
          initialData={modal.row}
          onSave={handleModalSave}
          saveLoading={createLoading || updateLoading}
        />
      )}

      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete deposit rule"
        itemName={deleteTarget?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

// ── Product Exceptions Tab ─────────────────────────────────
const ExceptionsTab = ({ data, setData, loading: listLoading }) => {
  const { showToast } = useToast();
  const [pendingChanges, setPendingChanges] = useState({});
  const [saveLoading, setSaveLoading]       = useState(false);
  const [modal, setModal]                   = useState(null);
  const [statusFilter, setStatusFilter]     = useState("");
  const [typeFilter, setTypeFilter]         = useState("");

  const hasPending = Object.keys(pendingChanges).length > 0;

  const applyToggle = (row) => {
    setPendingChanges((prev) => {
      const current = prev[row.id];
      const next = current !== undefined ? !current : !row.status;
      return { ...prev, [row.id]: next };
    });
  };

  const handleSaveToggles = async () => {
    setSaveLoading(true);
    try {
      setData((prev) =>
        prev.map((r) =>
          pendingChanges[r.id] !== undefined ? { ...r, status: pendingChanges[r.id] } : r
        )
      );
      setPendingChanges({});
      showToast("Exceptions saved successfully", "success");
    } catch { showToast("Failed to save", "error"); }
    finally { setSaveLoading(false); }
  };

  const handleModalSave = (form) => {
    if (modal?.row) {
      setData((prev) => prev.map((r) => r.id === modal.row.id ? { ...r, ...form } : r));
      showToast("Exception updated", "success");
    } else {
      setData((prev) => [...prev, { ...form, id: Date.now() }]);
      showToast("Exception added", "success");
    }
    setModal(null);
  };

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (typeFilter && r.productType !== typeFilter) return false;
      const s = getDisplayStatus(r, pendingChanges);
      if (statusFilter === "true"  && !s) return false;
      if (statusFilter === "false" &&  s) return false;
      return true;
    });
  }, [data, typeFilter, statusFilter, pendingChanges]);

  return (
    <>
      <div className={styles.tableSection}>
        <div className={styles.tableToolbar}>
          <div className={styles.filterBar}>
            <SelectBox
              placeholder="All Product Types"
              value={typeFilter}
              options={[
                { label: "All Product Types", value: "" },
                { label: "Flights",           value: "Flights" },
                { label: "Hotels",            value: "Hotels" },
                { label: "Insurance",         value: "Insurance" },
                { label: "Packages",          value: "Packages" },
              ]}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <SelectBox
              placeholder="Status: All"
              value={statusFilter}
              options={[
                { label: "Status: All", value: "" },
                { label: "Active",      value: "true" },
                { label: "Inactive",    value: "false" },
              ]}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          <button className={styles.add} onClick={() => setModal({ row: null })}>
            + Add Exception
          </button>
        </div>

        <div className={styles.resultsSummary}>
          Showing {filtered.length} of {data.length} exceptions
        </div>

        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Product Type</th>
                <th>Exception Type</th>
                <th>Alternative Rule</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr><td colSpan={5} className={styles.noResults}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className={styles.noResults}>No exceptions configured.</td></tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    <td><span className={styles.productBadge}>{row.productType}</span></td>
                    <td>
                      <span className={`${styles.pill} ${row.exceptionType === "no-deposit" ? styles.pillRed : styles.pillBlue}`}>
                        {row.exceptionType === "no-deposit" ? "No Deposit Required" : "Alternative Rule"}
                      </span>
                    </td>
                    <td>{row.alternativeRule || <span className={styles.na}>—</span>}</td>
                    <td>
                      <ToggleSwitch
                        checked={!!getDisplayStatus(row, pendingChanges)}
                        onChange={() => applyToggle(row)}
                      />
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button onClick={() => setModal({ row })}>Edit</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.footerBar}>
          <span className={styles.paginationInfo}>{data.length} exceptions configured</span>
          <button className={styles.saveChangesBtn} onClick={handleSaveToggles} disabled={!hasPending || saveLoading}>
            {saveLoading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {modal && (
        <AddExceptionModal
          open={!!modal}
          onClose={() => setModal(null)}
          initialData={modal.row}
          onSave={handleModalSave}
        />
      )}
    </>
  );
};

// ── Validity Tab ───────────────────────────────────────────
const ValidityTab = () => {
  const { showToast } = useToast();
  const [form, setForm] = useState({ active: true, startDate: "2025-01-01", endDate: "2025-12-31" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className={styles.tableSection}>
      <div className={styles.configBlock}>
        <h4 className={styles.configTitle}>Validity & Activation</h4>
        <p className={styles.configDesc}>
          Control when deposit and payment terms are active. Deposit rules outside their validity period will never be applied to bookings.
        </p>

        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>Start Date</label>
            <input type="date" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} />
          </div>
          <div className={styles.field}>
            <label>End Date</label>
            <input type="date" value={form.endDate} onChange={(e) => set("endDate", e.target.value)} />
          </div>
        </div>

        <div className={styles.toggleField}>
          <div>
            <strong>Active Status</strong>
            <p>When inactive, no deposit rules or payment terms will be applied to bookings.</p>
          </div>
          <div className={styles.toggleWrapper}>
            <span className={form.active ? styles.toggleLabelOn : styles.toggleLabelOff}>
              {form.active ? "Active" : "Inactive"}
            </span>
            <ToggleSwitch
              checked={form.active}
              onChange={() => set("active", !form.active)}
            />
          </div>
        </div>

        <div className={styles.infoCard}>
          <Clock size={14} />
          <span><strong>Note:</strong> Rules outside their validity period are never applied, regardless of their individual status.</span>
        </div>

        <div className={styles.configFooter}>
          <button
            className={styles.saveChangesBtn}
            onClick={() => showToast("Validity settings saved", "success")}
          >
            Save Validity
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────
const DepositPayment = () => {
  const [activeTab, setActiveTab] = useState("rules");
  const { data: rulesData, summary, loading: rulesLoading, refetch: refetchRules } = useDepositRules();
  const { data: exceptionsFromApi, loading: exceptionsLoading } = useDepositExceptions();
  const [exceptionsData, setExceptionsData] = useState([]);

  useEffect(() => {
    if (!exceptionsLoading) setExceptionsData(exceptionsFromApi ?? []);
  }, [exceptionsLoading, exceptionsFromApi]);

  const rules = rulesData ?? [];
  const totalRules = summary?.total_rules ?? rules.length;
  const activeRules = summary?.active_rules ?? rules.filter((r) => r.status).length;

  return (
    <div className={styles.container}>

      <div className={styles.toolbar}>
        <div className={styles.manager}>Deposit & Payment Terms</div>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon}>⚠</span>
        <span>Deposit rules determine prepayment amounts for bookings. Product exceptions override the default deposit behavior per product type.</span>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{totalRules}</span>
          <span className={styles.statLabel}>Total Rules</span>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statNum} ${styles.statGreen}`}>{activeRules}</span>
          <span className={styles.statLabel}>Active Rules</span>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statNum} ${styles.statGray}`}>{totalRules - activeRules}</span>
          <span className={styles.statLabel}>Inactive</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{exceptionsData?.length ?? 0}</span>
          <span className={styles.statLabel}>Exceptions</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon && tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "rules"      && <RulesTab      data={rulesData ?? []}      refetch={refetchRules}      loading={rulesLoading} />}
      {activeTab === "exceptions" && <ExceptionsTab data={exceptionsData} setData={setExceptionsData} loading={exceptionsLoading} />}
      {activeTab === "validity"   && <ValidityTab />}

    </div>
  );
};

export default DepositPayment;